/**
 * ConfigModal.tsx
 *
 * Settings modal that allows the user to customise the application experience.
 *
 * ## Sections
 *
 * ### Appearance
 * Three theme buttons: Default (parchment), Dark (black arcane), Light (ivory daylight).
 * The selected theme is applied immediately via `ThemeContext.setTheme` and persisted
 * in a cookie.
 *
 * **Hidden feature** — triple-clicking the "Light" button within 600 ms opens the
 * `CardPickerCalculator` in force-card mode, allowing the user to pin a specific card
 * to all dates for the current session.
 *
 * ### Delay
 * A range slider (1–10 minutes, 0.25-minute steps) that controls how long the speech
 * recognition controller waits before re-listening after a successful match.
 * Persisted in `localStorage` under `tarot-speech-delay`.
 *
 * ### Shuffle on Start
 * A toggle that, when enabled, runs `buildShuffledCalendar` on the next page load to
 * assign a randomised card-to-date mapping for the session.
 * Persisted in `localStorage` under `tarot-shuffle-on-start`.
 *
 * ### Deck to Use
 * Thumbnail buttons for each available deck artwork variant.
 * The active deck index is managed by `ThemeContext.setDeckIndex` and persisted in
 * `localStorage` under `tarot-deck-index`.
 *
 * ## Closing
 * - Clicking the backdrop (outside the modal panel) calls `onClose`.
 * - Pressing Escape calls `onClose` (or closes the nested calculator if it is open).
 * - The × button in the header calls `onClose`.
 */

'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useTheme, type Theme } from '@/lib/themeContext';
import { TAROT_CARDS } from '@/data/tarotData';
import CardPickerCalculator from './CardPickerCalculator';
import type { TarotCard } from '@/data/tarotData';

/** Props accepted by `ConfigModal`. */
interface ConfigModalProps {
  /** Called when the modal should be closed (after any exit animation). */
  onClose: () => void;
  /**
   * Called when the user selects a card via the hidden force-card calculator.
   * Receives the chosen `TarotCard`.
   */
  onCardPicked?: (card: TarotCard) => void;
  /** The card currently pinned as the force-override, shown as the active selection. */
  currentCard?: TarotCard;
}

/** Available theme options with display labels and descriptions. */
const THEMES: { value: Theme; label: string; desc: string }[] = [
  { value: 'default', label: 'Default', desc: 'Classic mystical parchment' },
  { value: 'dark', label: 'Dark', desc: 'Deep black arcane night' },
  { value: 'light', label: 'Light', desc: 'Bright ivory daylight' },
];

/** localStorage key for the speech-recognition delay preference (in minutes). */
const DELAY_STORAGE_KEY = 'tarot-speech-delay';

/** Default speech delay in minutes when no preference has been saved. */
const DELAY_DEFAULT = 2;

/** localStorage key for the shuffle-on-start boolean preference. */
const SHUFFLE_STORAGE_KEY = 'tarot-shuffle-on-start';

/** Index of the primary (Rider-Waite) deck in the `imageUrl` arrays. */
const TAROT_DISPLAY = 0; // Rider-Waite. When 1: Tarot de Marseille

/**
 * Format a delay value in minutes as a human-readable string.
 *
 * @param minutes - Delay duration in minutes (may be fractional).
 * @returns A string such as `"2 min"` or `"2:30 min"`.
 *
 * @example
 * formatDelay(2)    // "2 min"
 * formatDelay(2.5)  // "2:30 min"
 */
function formatDelay(minutes: number): string {
  const totalSeconds = Math.round(minutes * 60);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  if (s === 0) return `${m} min`;
  return `${m}:${s.toString().padStart(2, '0')} min`;
}

export default function ConfigModal({ onClose, onCardPicked, currentCard }: ConfigModalProps) {
  const { theme, setTheme, deckIndex, setDeckIndex } = useTheme();
  const [showCalculator, setShowCalculator] = useState(false);
  const [speechDelayMinutes, setSpeechDelayMinutes] = useState<number>(DELAY_DEFAULT);
  const [shuffleOnStart, setShuffleOnStart] = useState<boolean>(false);
  const deckOptions = TAROT_CARDS[0]?.imageUrl ?? [];

  // Load persisted delay on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(DELAY_STORAGE_KEY);
      if (stored !== null) {
        const parsed = parseFloat(stored);
        if (!isNaN(parsed) && parsed >= 1 && parsed <= 10) {
          setSpeechDelayMinutes(parsed);
        }
      }
    } catch { /* ignore */ }
    try {
      const storedShuffle = localStorage.getItem(SHUFFLE_STORAGE_KEY);
      if (storedShuffle !== null) {
        setShuffleOnStart(storedShuffle === 'true');
      }
    } catch { /* ignore */ }
  }, []);

  const handleDelayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    setSpeechDelayMinutes(val);
    try { localStorage.setItem(DELAY_STORAGE_KEY, String(val)); } catch { /* ignore */ }
  };

  const handleShuffleToggle = () => {
    const next = !shuffleOnStart;
    setShuffleOnStart(next);
    try { localStorage.setItem(SHUFFLE_STORAGE_KEY, String(next)); } catch { /* ignore */ }
  };

  // Triple-click detection for the "Light" button
  const lightClickCount = useRef(0);
  const lightClickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleLightClick = (e: React.MouseEvent) => {
    setTheme('light');
    lightClickCount.current += 1;
    if (lightClickTimer.current) clearTimeout(lightClickTimer.current);
    if (lightClickCount.current >= 3) {
      lightClickCount.current = 0;
      setShowCalculator(true);
      return;
    }
    lightClickTimer.current = setTimeout(() => {
      lightClickCount.current = 0;
    }, 600);
  };

  // Close on ESC
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showCalculator) {
          setShowCalculator(false);
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, showCalculator]);

  const handleCardPicked = (card: TarotCard) => {
    setShowCalculator(false);
    onCardPicked?.(card);
    onClose();
  };

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center modal-backdrop"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onPointerMove={(e) => e.stopPropagation()}
        onPointerUp={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Configuration">

        <div className="relative w-full max-w-lg mx-4 bg-card border border-border/60 rounded-2xl shadow-2xl overflow-hidden animate-modal-in"
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-border/40">
            <div className="flex items-center gap-2">
              <span className="text-primary text-sm animate-gold-pulse select-none" aria-hidden>✦</span>
              <h2 className="font-decorative text-base text-primary tracking-widest uppercase">Configuration</h2>
            </div>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-primary transition-colors duration-200 text-xl leading-none p-1 rounded"
              aria-label="Close configuration">
              ×
            </button>
          </div>

          {/* Scrollable body */}
          <div className="overflow-y-auto max-h-[75vh] scrollbar-arcane px-6 py-5 space-y-7">

            {/* ── Theme Section ── */}
            <section>
              <h3 className="font-display text-xs text-primary tracking-widest uppercase mb-3 flex items-center gap-2">
                <span aria-hidden>◈</span> Appearance
              </h3>
              <div className="grid grid-cols-3 gap-2">
                {THEMES.map((t) => {
                  const isLight = t.value === 'light';
                  return (
                    <button
                      key={t.value}
                      onClick={isLight ? handleLightClick : () => setTheme(t.value)}
                      className={`
                        relative flex flex-col items-center gap-1.5 p-3 rounded-xl border transition-all duration-200
                        ${theme === t.value
                          ? 'border-primary bg-primary/10 shadow-[0_0_12px_rgba(201,168,76,0.2)]'
                          : 'border-border/40 hover:border-primary/40 hover:bg-muted/30'}
                      `}
                      aria-pressed={theme === t.value}>
                      <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-xs
                        ${t.value === 'default' ? 'bg-[#1a1410] border-[#c9a84c]' : ''}
                        ${t.value === 'dark' ? 'bg-[#050505] border-[#c9a84c]' : ''}
                        ${t.value === 'light' ? 'bg-[#f5f0e8] border-[#8b6914]' : ''}
                      `}>
                        {theme === t.value && <span className="text-primary text-[10px]">✓</span>}
                      </div>
                      <span className="font-display text-[11px] text-foreground tracking-wider">{t.label}</span>
                      <span className="font-body text-[9px] text-muted-foreground text-center leading-tight">{t.desc}</span>
                    </button>
                  );
                })}
              </div>

              {/* ── Delay Slider ── */}
              <div className="mt-5">
                <div className="flex items-center justify-between mb-2">
                  <label htmlFor="speech-delay-slider" className="font-display text-xs text-foreground tracking-wider">
                    Delay
                  </label>
                  <span className="font-body text-xs text-primary tabular-nums">
                    {formatDelay(speechDelayMinutes)}
                  </span>
                </div>
                <input
                  id="speech-delay-slider"
                  type="range"
                  min={1}
                  max={10}
                  step={0.25}
                  value={speechDelayMinutes}
                  onChange={handleDelayChange}
                  className="w-full h-1.5 rounded-full appearance-none cursor-pointer accent-primary bg-border/40"
                  aria-label="Speech recognition delay in minutes"
                />
                <div className="flex justify-between mt-1">
                  <span className="font-body text-[9px] text-muted-foreground">1 min</span>
                  <span className="font-body text-[9px] text-muted-foreground">10 min</span>
                </div>
              </div>

              {/* ── Shuffle on Start Slider ── */}
              <div className="mt-5 flex items-center justify-between">
                <label htmlFor="shuffle-on-start-toggle" className="font-display text-xs text-foreground tracking-wider cursor-pointer select-none">
                  Shuffle cards on start?
                </label>
                <label htmlFor="shuffle-on-start-toggle" className="relative inline-flex h-6 w-11 cursor-pointer items-center">
                  <input
                    id="shuffle-on-start-toggle"
                    type="checkbox"
                    checked={shuffleOnStart}
                    onChange={handleShuffleToggle}
                    className="sr-only"
                    aria-label="Shuffle cards on start"
                  />
                  <span className={`block h-6 w-11 rounded-full border border-border/50 transition-colors duration-200 ${shuffleOnStart ? 'bg-primary' : 'bg-background/80'}`} />
                  <span
                    className={`absolute left-0.5 top-0.5 inline-block h-5 w-5 transform rounded-full bg-background border border-border/50 shadow transition-transform duration-200 ${shuffleOnStart ? 'translate-x-5' : 'translate-x-0'}`}
                  />
                </label>
              </div>

              {/* ── Deck to use ── */}
              <div className="mt-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-display text-xs text-primary tracking-widest uppercase">Deck to use</span>
                </div>
                <div className="flex gap-2">
                  {deckOptions.map((src, index) => (
                    <button
                      key={`${src}-${index}`}
                      type="button"
                      onClick={() => setDeckIndex(index)}
                      className={`relative h-32 w-24 overflow-hidden rounded-none border transition-all duration-200 ${deckIndex === index ? 'border-primary border-[3px] shadow-[0_0_10px_rgba(201,168,76,0.25)]' : 'border-border/40 border'} `}
                      aria-pressed={deckIndex === index}
                      aria-label={`Select deck ${index}`}
                    >
                      <img
                        src={src}
                        alt={`Deck ${index} sample image`}
                        className="h-full w-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              </div>

              {/* Tarots to use */}
            </section>

          </div>
        </div>
      </div>

      {/* Card Picker Calculator — rendered above the config modal */}
      {showCalculator && (
        <CardPickerCalculator
          onConfirm={handleCardPicked}
          onClose={() => setShowCalculator(false)}
          currentCardName={currentCard?.name}
        />
      )}
    </>
  );
}
